import designTokens from '@customink/design-tokens/build/ci_light/js/designTokens';
import { Carousel, Dialog, DialogContent, useMobileBreakpoint, Box } from '@customink/pigment-react';
import { useState, useMemo } from 'react';
import { OrderSummary } from '~/adapters/services/accounts/orders/types';
import { addDataLayerEvent } from '~/adapters/tracking/tracking';
import { OrderChip } from '~/components/Chip/OrderChip';
import { productCarouselImages } from '~/adapters/services/mms/links';
import { getDesignCId } from '~/adapters/services/accounts/orders/links';

interface OrderProductsCarouselProps {
    order: OrderSummary;
}

const addTrackingEvent = (event: { interaction_label: string; design_proof_view: string }) => {
    addDataLayerEvent({
        event: 'accounts_interaction',
        interaction_type: 'button_click',
        interaction_location: 'order_history',
        ...event,
    });
};

export function OrderProductsCarousel({ order }: OrderProductsCarouselProps) {
    const [isCarouselDialogOpen, setIsCarouselDialogOpen] = useState<boolean>(false);
    const isMobile = useMobileBreakpoint();

    const handleCarouselClick = () => {
        addTrackingEvent({ interaction_label: 'design_proof_open', design_proof_view: 'standard' });
        setIsCarouselDialogOpen(true);
    };

    const handleCarouselArrowClick = () => {
        addTrackingEvent({
            interaction_label: 'design_proof_arrow_left or design_proof_arrow_right',
            design_proof_view: 'standard',
        });
    };

    const handleCarouselDialogClose = () => {
        addTrackingEvent({ interaction_label: 'design_proof_close', design_proof_view: 'enlarged' });
        setIsCarouselDialogOpen(false);
    };

    const handleExpandecCarouselArrowClick = () => {
        addTrackingEvent({
            interaction_label: 'design_proof_arrow_left or design_proof_arrow_right',
            design_proof_view: 'enlarged',
        });
    };

    // proofs are legacy images, we want to use mms-images-lambda, OrderService should handle this
    const carouselImages = useMemo(
        () =>
            productCarouselImages(
                // if there is no productId then we will use styleId
                order.orderItems.map((item) => ({
                    productId: item.productId?.toString() || item.styleId.toString(),
                    compositeId: getDesignCId(order.designId, order.account.id),
                    isFrontPrintable: !!item.designProof?.frontUrl,
                    isBackPrintable: !!item.designProof?.backUrl,
                })),
                new Date(order.placedAt),
            ),
        [order],
    );

    const slides = useMemo(
        () =>
            carouselImages.map((uri) => (
                <img
                    alt="Product preview"
                    src={uri}
                    key={uri}
                    style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                    }}
                />
            )),
        [carouselImages],
    );

    return (
        <>
            <Dialog
                open={isCarouselDialogOpen}
                title={`Order #${order.id} · ${order.designName}`}
                titleSx={{ color: designTokens.color.shade.black.value, opacity: 0.86, pointerEvents: 'none' }}
                onClose={handleCarouselDialogClose}
            >
                <DialogContent
                    style={{
                        padding: isMobile ? '8px' : '20px 24px',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                    sx={{
                        '& .thumbnails .thumbnail.selected:before': {
                            border: `1px solid ${designTokens.color.blue.default.value} !important`,
                        },
                    }}
                >
                    <Carousel
                        onNextClick={handleExpandecCarouselArrowClick}
                        onPreviousClick={handleExpandecCarouselArrowClick}
                        slides={slides}
                        sliderHeight="auto"
                        loop={false}
                        thumbnails={slides}
                        navigatorSide="bottom"
                    />
                </DialogContent>
            </Dialog>
            <Box sx={{ position: 'relative', width: isMobile ? '100%' : '336px' }}>
                <OrderChip order={order} sx={{ position: 'absolute', top: '16px', left: '16px', zIndex: 1 }} />
                <Carousel
                    onSlideClick={handleCarouselClick}
                    onNextClick={handleCarouselArrowClick}
                    onPreviousClick={handleCarouselArrowClick}
                    slides={slides}
                    loop={false}
                    sliderBg="gray"
                    sx={{
                        '& .slider': {
                            width: '100%',
                            height: '228px',
                            borderRadius: '8px',
                        },

                        '& .keen-slider__slide': { cursor: 'zoom-in !important' },
                    }}
                />
            </Box>
        </>
    );
}
